"use client";

import { Core } from "@faire/design-tokens";
import { AspectRatioPicture } from "@faire/retailer-visitor-shared/components/Image/AspectRatioPicture";
import {
  CarouselDots,
  DotsColorVariant,
} from "@faire/retailer-visitor-shared/components/ImageCarousel/CarouselDots";
import { PAGE_TYPE } from "@faire/retailer-visitor-shared/consts/PAGE_TYPE";
import { getAltTag } from "@faire/retailer-visitor-shared/features/Marketplace/utils";
import { isTouchDevice } from "@faire/web/common/isTouchDevice";
import { useStrictLocalization } from "@faire/web/common/localization";
import { TelemetryManager } from "@faire/web/common/opentelemetry/TelemetryManager";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeProductImageUrl } from "@faire/web/common/transform-images";
import { Chevron } from "@faire/web/slate/icons/foundation/Chevron";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

type ImageSelectAction = "leftNav" | "rightNav" | "dot" | "swipe" | undefined;
interface IProps {
  children?: React.ReactNode | undefined;
  /** Fallback used in case an image has no `name`  */
  name?: string;
  /** List of images with name (name is used to populate `alt`). These images are an extension of `IImage` with `{ name?: string }` */
  images: INamedImage[];
  /** Fire an event when an image is scrolled to (selected)
   * - `index` - the index of the image
   * - `action` - the action that selected the image (swipe, leftNav, rightNav, dot)
   */
  onImageSelected?: (index: number, action: ImageSelectAction) => void;
  /**
   * Wrapper around the image only. This can be used to add routing or other clickable features
   *
   * i.e
   * ```tsx
   * <ImageCarousel
   *   ImageWrapper={props => <Link to='/' {...props} />}
   * />
   * ```
   */
  ImageWrapper?: React.ComponentType<{ children: React.ReactNode }>;
  /** Hide the dot when there is a single image */
  hideDotsWhenSingleImage?: boolean;
  /** Render border around the image */
  withBorder?: boolean;
  /** h1 of the page, used in alt tag */
  title?: string | undefined;
  /** page type, used in alt tag */
  pageType?: PAGE_TYPE;
  otelSpanName?: string;
  onImageScrolled?: (index: number, action: string) => void;
  /** The color variant for the dots */
  dotsColorVariant?: DotsColorVariant;
}

export interface INamedImage extends IImage {
  name?: string;
}

export namespace INamedImage {
  export const build = (partial?: Partial<INamedImage>) => {
    return {
      ...IImage.build(partial),
      name: partial?.name,
    };
  };
}

export const ImageCarousel: React.FC<IProps> = ({
  images,
  ImageWrapper,
  withBorder,
  pageType,
  name,
  title,
  children,
  hideDotsWhenSingleImage,
  onImageSelected,
  otelSpanName,
  onImageScrolled,
  dotsColorVariant,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const isSSR = useIsSSR();

  // Set images array to a ref to save re-rendering if the reference to the array changes
  const imagesRef = useRef(images);
  imagesRef.current = images;

  const onImageSelectedRef = useRef(onImageSelected);
  onImageSelectedRef.current = onImageSelected;

  const imageSelectActionRef = useRef<ImageSelectAction>(undefined);

  // Don't enable the carousel if there is only one image
  const shouldRenderCarousel = imagesRef.current.length > 1;

  // Only render the first image
  const [shouldRenderAll, setShouldRenderAll] = useState(false);

  const showNavigationButtons = !isTouchDevice() && shouldRenderCarousel;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const slideInnerContainerRef = useRef<HTMLDivElement | null>(null);
  const refCallback = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      slideInnerContainerRef.current = node;
      const onScroll = () => {
        const scrollLeft = node.scrollLeft;
        const clientWidth = node.clientWidth;
        const index = Math.round(scrollLeft / clientWidth);
        setSelectedIndex(index);
        setShouldRenderAll(true);
      };
      slideInnerContainerRef.current.addEventListener("scroll", onScroll);
    }
  }, []);

  const scrollToIndex = useCallback((requestedIndex: number) => {
    if (slideInnerContainerRef.current) {
      let index = requestedIndex;
      const maxIndex = imagesRef.current.length - 1;
      if (index < 0) {
        index = maxIndex;
      } else if (index > maxIndex) {
        index = 0;
      }
      slideInnerContainerRef.current.scrollTo?.({
        left: index * slideInnerContainerRef.current.clientWidth,
        behavior: "smooth",
      });
    }
  }, []);

  const scrollPrev = useCallback(() => {
    imageSelectActionRef.current = "leftNav";
    scrollToIndex(selectedIndex - 1);
    onImageScrolled?.(selectedIndex, "leftNav");
  }, [onImageScrolled, scrollToIndex, selectedIndex]);

  const scrollNext = useCallback(() => {
    imageSelectActionRef.current = "rightNav";
    scrollToIndex(selectedIndex + 1);
    onImageScrolled?.(selectedIndex, "rightNav");
  }, [onImageScrolled, scrollToIndex, selectedIndex]);

  const scrollTo = useCallback(
    (index: number) => {
      imageSelectActionRef.current = "dot";
      scrollToIndex(index);
      onImageScrolled?.(index, "dot");
    },
    [onImageScrolled, scrollToIndex]
  );

  useEffect(() => {
    if (imageSelectActionRef.current) {
      onImageSelectedRef.current?.(selectedIndex, imageSelectActionRef.current);
    }
    imageSelectActionRef.current = "swipe";
  }, [selectedIndex]);

  const ImageWrapperComponent = ImageWrapper ?? React.Fragment;

  return (
    <Container>
      <ImageWrapperComponent>
        <SliderContainer>
          <SliderInnerContainer ref={refCallback}>
            {imagesRef.current.map((image, index) => {
              const imageUrl = optimizeProductImageUrl(image, isSSR);
              let span: string | undefined = undefined;
              if (otelSpanName) {
                span = TelemetryManager.get().startManagedSpan(otelSpanName);

                if (!imageUrl && span) {
                  TelemetryManager.get().endManagedSpan(span);
                }
              }
              return (
                <ImageContainer
                  key={image.token ?? index}
                  $withBorder={withBorder}
                >
                  {shouldRenderAll || index === 0 ? (
                    <AspectRatioPicture $width="100%" $ratio={1} $hideBorder>
                      <Image
                        alt={
                          getAltTag(
                            {
                              productName: image.name ?? name,
                              pageType: pageType,
                              title: title,
                            },
                            strictLocalize
                          ) ??
                          image.name ??
                          name
                        }
                        src={imageUrl}
                        onLoad={() => {
                          if (span) {
                            TelemetryManager.get().endManagedSpan(span);
                          }
                        }}
                      />
                    </AspectRatioPicture>
                  ) : null}
                </ImageContainer>
              );
            })}
          </SliderInnerContainer>
          <ImageOverlayContainer>{children}</ImageOverlayContainer>
        </SliderContainer>
      </ImageWrapperComponent>
      {showNavigationButtons ? (
        <>
          <ChevronContainer
            $position="left"
            onClick={scrollPrev}
            role="button"
            aria-label={strictLocalize({
              defaultMessage: "Previous Carousel Slide",
              description: {
                text: "Aria label for the previous carousel slide button",
              },
            })}
          >
            <Chevron direction="left" strokeWidth={2} />
          </ChevronContainer>
          <ChevronContainer
            $position="right"
            onClick={scrollNext}
            role="button"
            aria-label={strictLocalize({
              defaultMessage: "Next Carousel Slide",
              description: {
                text: "Aria label for the next carousel slide button",
              },
            })}
          >
            <Chevron direction="right" strokeWidth={2} />
          </ChevronContainer>
        </>
      ) : null}
      <Spacer height="1x" />
      {hideDotsWhenSingleImage && !shouldRenderCarousel ? null : (
        <CarouselDots
          onClick={(index) => {
            scrollTo(index);
          }}
          currentImageIndex={selectedIndex}
          numValidImages={imagesRef.current.length}
          colorVariant={dotsColorVariant}
        />
      )}
    </Container>
  );
};

const ChevronContainer = styled(Row).attrs({
  align: "center",
  justify: "center",
})<{ $position: "left" | "right" }>`
  width: 32px;
  height: 32px;
  top: 42%;
  ${({ $position: position }) =>
    position === "left" ? "left: 0" : "right: 0"};

  position: absolute;
  cursor: pointer;
  z-index: 2;
  opacity: 0;
  transition: 0.5s;
`;

const Container = styled(Column)`
  flex-shrink: 0;
  position: relative;
  overflow: hidden;

  user-select: none;
  -moz-user-select: none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  -o-user-select: none;

  &:hover {
    ${ChevronContainer} {
      opacity: 1;
      background-color: rgba(255, 255, 255, 0.75);
    }
  }
`;

const SliderContainer = styled.div`
  overflow: hidden;
  position: relative;
`;

const SliderInnerContainer = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ImageOverlayContainer = styled.div`
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  position: absolute;

  pointer-events: none;
`;

export const ImageContainer = styled.div<{ $withBorder?: boolean }>`
  position: relative;
  scroll-snap-align: center;
  scroll-snap-stop: always;
  min-width: 0;
  background: ${Core.surface.muted};
  ${(props) =>
    props.$withBorder
      ? css`
          border: 1px solid ${Core.border.muted};
        `
      : css``}
  flex: 0 0 100%;
`;

const Image = styled.img`
  display: block;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;
